"use client";

import { LegalLink, LegalLinks } from "@/components/legal/legal-links";
import { Group, Line, SectionHeader, Switch } from "./atoms";
import { SET } from "./palette";

interface MiscSectionProps {
  welcomeEnabled: boolean;
  onToggleWelcome: (next: boolean) => void;
  analyticsEnabled: boolean;
  /** `false` quand le stockage a refusé le choix : il ne vaut que pour la session. */
  analyticsChoiceKept: boolean;
  onToggleAnalytics: (next: boolean) => void;
  /** Les liens d'ici mènent hors du Studio : le calque doit se refermer,
   *  sinon la page légale se rend dessous et le clic paraît sans effet. */
  onClose: () => void;
}

export function MiscSection({
  welcomeEnabled,
  onToggleWelcome,
  analyticsEnabled,
  analyticsChoiceKept,
  onToggleAnalytics,
  onClose,
}: MiscSectionProps) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <SectionHeader kicker="Divers" />

      <Group>
        <Line
          label="Écran d'accueil au démarrage"
          hint="Affiche le carton de bienvenue et joue le jingle à chaque rechargement"
          control={
            <Switch
              checked={welcomeEnabled}
              onChange={onToggleWelcome}
              label="Écran d'accueil au démarrage"
            />
          }
        />

        {/*
          Droit d'opposition. La mesure d'usage repose sur l'intérêt légitime,
          pas sur le consentement — elle n'écrit rien sur l'appareil et ne sert
          qu'à améliorer Virtuosa. Ce commutateur reste indispensable : sans
          moyen simple de s'y opposer, la base légale ne tiendrait pas.
        */}
        <Line
          withDivider={false}
          label="Contribuer aux statistiques d'usage"
          hint={
            <>
              Mesure anonyme de l&apos;utilisation, pour améliorer Virtuosa. Aucun
              cookie, aucun traceur publicitaire.{" "}
              <LegalLink
                href="/confidentialite"
                label="En savoir plus"
                onNavigate={onClose}
                style={{ color: SET.violetLight }}
              />
            </>
          }
          control={
            <Switch
              checked={analyticsEnabled}
              onChange={onToggleAnalytics}
              label="Contribuer aux statistiques d'usage"
            />
          }
        />

        {/*
          Le choix n'a pas pu être enregistré : navigation privée, données de
          site bloquées, quota plein. Il est honoré pour la session, et c'est
          tout ce qu'on peut promettre. Le taire laisserait croire à un refus
          définitif, alors qu'il repart au prochain chargement.

          `role="status"` parce que la ligne apparaît après coup, en réponse
          au commutateur : sans lui, elle passerait inaperçue au lecteur
          d'écran, qui est précisément la personne qui ne verra rien.
        */}
        {analyticsChoiceKept ? null : (
          <p
            role="status"
            style={{
              margin: 0,
              padding: "0 14px 10px",
              fontSize: 11,
              lineHeight: "16px",
              color: SET.amber,
            }}
          >
            Ce navigateur refuse d&apos;enregistrer votre choix. Il est bien appliqué,
            mais seulement jusqu&apos;à la fermeture de l&apos;onglet.
          </p>
        )}
      </Group>

      {/*
        Les trois pages légales, en pied de réglages : c'est là qu'on les
        cherche sur une application, et c'est la convention iOS. Elles ne sont
        plus seules ici pour autant — le Studio ne couvre ni `/learn` ni la
        bibliothèque, et la coque iOS demande un chemin qui ne dépende d'aucun
        écran (voir `legal-links.tsx`).

        Les conditions générales manquaient : la ligne n'en portait que deux
        sur trois, ce qui est précisément ce que le composant partagé évite
        désormais.
      */}
      <LegalLinks
        onNavigate={onClose}
        className="text-[11px] leading-[17px]"
        style={{ color: SET.textMute }}
      />
    </section>
  );
}
